import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useNavigate, useLocation } from "react-router-dom";

// Import the pulse survey form components that are already being used
import EmailTemplate from "./SurveyFormCategories/EmailTemplate";
import AssignSection from "./SurveyFormCategories/Assign";
import ReportAccessSection from "./SurveyFormCategories/ReportAccess";

// Import the survey context
import { useSurveyContext } from "./Data/SurveyFormContext";

// Icons
const ArrowLeftIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
  </svg>
)

const CalendarIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z" />
  </svg>
)

const EmailIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M20,8L12,13L4,8V6L12,11L20,6M20,4H4C2.89,4 2,4.89 2,6V18A2,2 0 0,0 4,20H20A2,2 0 0,0 22,18V6C0,4.89 21.1,4 20,4Z" />
  </svg>
)

const UsersIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
  </svg>
)

const ReportIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
  </svg>
)

const PulseScheduleSurvey = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Get survey context functions
  const { formData, updateFormData, handleSubmit, saveDraft, loading } =
    useSurveyContext();

  // Get initial survey data from navigation state (from SurveyLibrary or existing draft from PulseBuilder)
  const initialSurveyData = location.state?.initialSurveyData || null;
  const existingDraftData = location.state?.existingDraftData || null;

  // Determine if we're working with an existing draft or creating from template
  const surveyData = existingDraftData || initialSurveyData;
  const isExistingDraft = Boolean(existingDraftData);

  // Step management - extended to support 3 steps (1 = Email Details, 2 = Assign Recipients, 3 = Report Access)
  const [wizardStep, setWizardStep] = useState(1);
  
  // Track if we're sending (not just saving draft) to control navigation
  const [isSending, setIsSending] = useState(false);

  // Email template state (from original QuickSend modal)
  const [emailTpl, setEmailTpl] = useState({
    email_subject:
      surveyData?.emailSubject ||
      formData?.emailSubject ||
      `Survey: ${surveyData?.name || formData?.name || ""}`,
    email_content:
      surveyData?.emailBody ||
      formData?.emailBody ||
      `Hi,\n\nYou have been invited to participate in the survey "${
        surveyData?.name || formData?.name || ""
      }". Please follow the link to complete the survey.\n\nThank you!`,
  });

  // Assignment state (from original QuickSend modal)
  const [assignedUsersList, setAssignedUsersList] = useState(
    surveyData?.assignedUsersList || formData?.assignedUsersList || []
  );

  // Report access collaborators state
  const [reportAccessUsers, setReportAccessUsers] = useState(
    surveyData?.reportAccessUsers || formData?.collaborators || []
  );

  // Survey data - use formData from context if available, otherwise fallback to surveyData
  const [quickSurvey] = useState(formData?.name ? formData : surveyData);

  // Updated navigation handlers for 3-step wizard
  const goToAssign = () => {
    setWizardStep(2);
  };

  const goToReportAccess = () => {
    setWizardStep(3);
  };

  const goBackToEmail = () => {
    setWizardStep(1);
  };

  const goBackToAssign = () => {
    setWizardStep(2);
  };

  // Update form data in context when local state changes
  useEffect(() => {
    if (surveyData) {
      updateFormData({
        ...surveyData,
        name: surveyData.name || "",
        description: surveyData?.user_tasks?.find(
              (task) => task && task.task_type === "description"
            )?.text || surveyData.draftData?.description || surveyData.description || "",
        questions: surveyData.questions || [],
        emailSubject: emailTpl.email_subject,
        emailBody: emailTpl.email_content,
        assignedUsersList,
        collaborators: reportAccessUsers,
        isExistingDraft, // Pass this flag to context
      });
    }
  }, [
    emailTpl.email_subject,
    emailTpl.email_content,
    assignedUsersList,
    reportAccessUsers,
    surveyData,
    isExistingDraft,
    updateFormData,
  ]);

  const handleSaveDraft = () => {
    if (quickSurvey) {
      // Update the form data with current wizard state before saving draft
      updateFormData({
        ...quickSurvey,
        name: quickSurvey.name || "",
        description: quickSurvey?.user_tasks?.find(
              (task) => task && task.task_type === "description"
            )?.text || quickSurvey.draftData?.description || quickSurvey.description || "",
        questions: quickSurvey.questions || [],
        emailSubject: emailTpl.email_subject,
        emailBody: emailTpl.email_content,
        assignedUsersList,
        collaborators: reportAccessUsers,
        isExistingDraft, // Pass this flag to context
      });

      // Use the context's saveDraft function with isTemplate = false for drafts
      saveDraft(false);
    }
  };

  const sendNow = () => {
    if (quickSurvey) {
      // Set the sending flag to control navigation
      setIsSending(true);
      
      // Update the form data with current wizard state before submitting
      updateFormData({
        ...quickSurvey,
        name: quickSurvey.name || "",
        description: quickSurvey.description || quickSurvey.draftData?.description || "",
        questions: quickSurvey.questions || [],
        emailSubject: emailTpl.email_subject,
        emailBody: emailTpl.email_content,
        assignedUsersList,
        collaborators: reportAccessUsers,
      });

      // Use the context's handleSubmit function to create the survey
      handleSubmit();
    }
  };

  const closeQuickSend = () => {
    // If we just sent a survey (not just saved draft), go to main schedule page
    if (isSending) {
      navigate("/leader/audit/schedule-v2");
      return;
    }

    // Check if we have a returnTo path in location state
    const returnTo = location.state?.returnTo;
    if (returnTo) {
      navigate(returnTo, { replace: true });
      return;
    }

    // Check if we came from PulseBuilder (existingDraftData indicates this)
    if (existingDraftData) {
      // If we have a survey ID, go to edit mode, otherwise go to pulse main page
      const surveyId = existingDraftData.id || formData?.id;
      if (surveyId) {
        navigate(`/leader/audit/schedule-v2/pulse/edit/${surveyId}`, { 
          state: { returnTo: "/leader/audit/schedule-v2", from: "schedule" }
        });
      } else {
        navigate("/leader/audit/schedule-v2/pulse");
      }
      return;
    }

    // Default fallback - go to library if we have survey data, otherwise schedule hub
    if (initialSurveyData) {
      navigate("/leader/audit/schedule-v2/pulse/library");
    } else {
      navigate("/leader/audit/schedule-v2");
    }
  };

  // Redirect if no survey data
  if (!quickSurvey) {
    navigate("/leader/audit/schedule-v2/pulse/library");
    return null;
  }

  return (
    <Container>
      {/* Header */}
      <HeaderSection>
        <BackButton onClick={closeQuickSend}>
          <ArrowLeftIcon />
          <span>Back</span>
        </BackButton>

        <HeaderContent>
          <IconWrapper>
            <CalendarIcon />
          </IconWrapper>
          <HeaderText>
            <Title>
              {isExistingDraft ? "Send Survey" : "Schedule Survey"}:{" "}
              {quickSurvey.name}
            </Title>
            <Subtitle>
              {wizardStep === 1
                ? "Step 1: Configure email template"
                : wizardStep === 2
                ? "Step 2: Select survey recipients"
                : "Step 3: Configure report access (optional)"}
            </Subtitle>
          </HeaderText>
        </HeaderContent>

        {/* Progress Indicator */}
        <ProgressIndicator>
          <ProgressStep active={wizardStep === 1} completed={wizardStep > 1}>
            <ProgressStepNumber
              active={wizardStep === 1}
              completed={wizardStep > 1}
            >
              1
            </ProgressStepNumber>
            <ProgressStepText>Email Details</ProgressStepText>
          </ProgressStep>
          <ProgressConnector completed={wizardStep > 1} />
          <ProgressStep active={wizardStep === 2} completed={wizardStep > 2}>
            <ProgressStepNumber
              active={wizardStep === 2}
              completed={wizardStep > 2}
            >
              2
            </ProgressStepNumber>
            <ProgressStepText>Assign Recipients</ProgressStepText>
          </ProgressStep>
          <ProgressConnector completed={wizardStep > 2} />
          <ProgressStep active={wizardStep === 3}>
            <ProgressStepNumber active={wizardStep === 3}>3</ProgressStepNumber>
            <ProgressStepText>Report Access</ProgressStepText>
          </ProgressStep>
        </ProgressIndicator>
      </HeaderSection>

      {/* Step Content */}
      <FormContainer>
        <StepFormLayout>
          <StepContent>
            <PanelSection>
              <SectionTitle>
                {wizardStep === 1 ? (
                  <>
                    <EmailIcon />
                    <span>Email Template</span>
                  </>
                ) : wizardStep === 2 ? (
                  <>
                    <UsersIcon />
                    <span>
                      Select Recipients ({assignedUsersList.length} selected)
                    </span>
                  </>
                ) : (
                  <>
                    <ReportIcon />
                    <span>
                      Report Access ({reportAccessUsers.length} users with
                      access)
                    </span>
                  </>
                )}
              </SectionTitle>

              {wizardStep === 1 && (
                <EmailTemplate
                  emailTemplate={emailTpl}
                  onUpdate={setEmailTpl}
                  errors={{}}
                />
              )}

              {wizardStep === 2 && (
                <AssignSection
                  formData={{
                    assignees: assignedUsersList,
                    draft: assignedUsersList,
                  }}
                  onUpdate={setAssignedUsersList}
                />
              )}

              {wizardStep === 3 && (
                <ReportAccessSection
                  formData={{
                    collaborators: reportAccessUsers,
                  }}
                  onUpdate={setReportAccessUsers}
                />
              )}
            </PanelSection>
          </StepContent>

          {/* Navigation - updated for 3-step wizard */}
          <StepNavigationSection>
            <LeftButtons>
              <SaveDraftButton
                onClick={handleSaveDraft}
                disabled={
                  loading || !emailTpl.email_subject || !emailTpl.email_content
                }
              >
                {loading ? "Saving..." : "Save Draft"}
              </SaveDraftButton>
            </LeftButtons>

            <RightButtons>
              {wizardStep === 1 && (
                <NextButton
                  onClick={goToAssign}
                  disabled={!emailTpl.email_subject || !emailTpl.email_content}
                >
                  Next
                </NextButton>
              )}

              {wizardStep === 2 && (
                <>
                  <BackButton onClick={goBackToEmail}>Back</BackButton>
                  <NextButton
                    onClick={goToReportAccess}
                    disabled={assignedUsersList.length === 0}
                  >
                    Next
                  </NextButton>
                </>
              )}

              {wizardStep === 3 && (
                <>
                  <BackButton onClick={goBackToAssign}>Back</BackButton>
                  <SendButton
                    onClick={sendNow}
                    disabled={assignedUsersList.length === 0 || loading}
                  >
                    {loading ? "Sending..." : "Send"}
                  </SendButton>
                </>
              )}
            </RightButtons>
          </StepNavigationSection>
        </StepFormLayout>
      </FormContainer>
    </Container>
  );
};

export default PulseScheduleSurvey;

// Styled Components - designed to look like a page instead of modal
const Container = styled.div`
  padding: 16px;
  max-width: 1400px;
  margin: 0 auto;
  animation: fadeIn 0.6s ease-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(12px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const HeaderSection = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e0e0e0;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: transparent;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  color: #7e7e7e;
  font-family: "Poppins";
  font-weight: 400;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f8f9fa;
    border-color: #476dfa;
    color: #476dfa;
  }
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  padding: 8px;
  background: #f8f9fa;
  border-radius: 6px;
`;

const HeaderText = styled.div``;

const Title = styled.h1`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 24px;
  color: #414141;
  margin: 0 0 4px 0;
`;

const Subtitle = styled.p`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 13px;
  color: #7e7e7e;
  margin: 0;
`;

const FormContainer = styled.div`
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  overflow: hidden;
`;

const PanelSection = styled.div`
  margin-bottom: 0;
`;

const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: "Poppins";
  font-weight: 600;
  font-size: 16px;
  color: #414141;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f0f0f0;
`;

// Progress Indicator Components
const ProgressIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ProgressStep = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
`;

const ProgressStepNumber = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: "Poppins";
  font-weight: 600;
  font-size: 12px;
  transition: all 0.2s;
  
  ${props => {
    if (props.completed) {
      return `
        background: #2e7d2e;
        color: white;
        border: 2px solid #2e7d2e;
      `;
    } else if (props.active) {
      return `
        background: #476dfa;
        color: white;
        border: 2px solid #476dfa;
      `;
    } else {
      return `
        background: #f0f0f0;
        color: #999;
        border: 2px solid #e0e0e0;
      `;
    }
  }}
`;

const ProgressStepText = styled.div`
  font-family: "Poppins";
  font-weight: 500;
  font-size: 11px;
  color: #7e7e7e;
  text-align: center;
  white-space: nowrap;
`;

const ProgressConnector = styled.div`
  width: 40px;
  height: 2px;
  background: ${props => props.completed ? '#2e7d2e' : '#e0e0e0'};
  transition: background-color 0.2s;
`;

// Step Layout Components
const StepFormLayout = styled.div`
  min-height: 500px;
  display: flex;
  flex-direction: column;
`;

const StepContent = styled.div`
  flex: 1;
  padding: 24px;
`;

const StepNavigationSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-top: 1px solid #f0f0f0;
  background: #fafafa;
`;

const LeftButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const RightButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const CancelButton = styled.button`
  padding: 8px 18px;
  background: #f0f0f0;
  color: #7e7e7e;
  border: none;
  border-radius: 7px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.18s;

  &:hover {
    background: #e0e0e0;
  }
`;

const SaveDraftButton = styled.button`
  padding: 8px 18px;
  background: ${props => props.disabled ? '#cccccc' : '#28a745'};
  color: white;
  border: none;
  border-radius: 7px;
  font-size: 15px;
  font-weight: 600;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: background 0.18s;

  &:hover:not(:disabled) {
    background: #218838;
  }
`;

const NextButton = styled.button`
  padding: 8px 18px;
  background: ${props => props.disabled ? '#cccccc' : '#476dfa'};
  color: white;
  border: none;
  border-radius: 7px;
  font-size: 15px;
  font-weight: 600;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: background 0.18s;

  &:hover:not(:disabled) {
    background: #3d5ce6;
  }
`;

const SendButton = styled.button`
  padding: 8px 18px;
  background: ${props => props.disabled ? '#cccccc' : '#476dfa'};
  color: white;
  border: none;
  border-radius: 7px;
  font-size: 15px;
  font-weight: 600;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: background 0.18s;

  &:hover:not(:disabled) {
    background: #3d5ce6;
  }
`;